import { FC } from "react";
import useEvent from "react-use-event-hook";
import styled from "styled-components";

import {
  DATA_E2E_KEY,
  IEditFormTabContainer,
  IStyledAttributes,
} from "@ryde/shared";
import {
  blueGreyMui,
  blueMui,
  greyMui,
  MOBILE_BREAKPOINT,
  redMui,
  rydeColors,
  tealMui,
} from "@ryde/theme";

interface ILayoutRightTabs {
  tabIndex?: number;
  setTabIndex?: (num: number) => void;
  tabs?: IEditFormTabContainer[];
}

const TabWrapperStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "tabs-wrapper",
})`
  display: flex;
  gap: 25px;
  align-items: center;
  font-weight: 700;
  grid-column: 4;
`;

const TabStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "tab",
})`
  width: 40px;
  border-bottom: 2px solid ${rydeColors.lightGreen};
  text-align: center;
  padding: 3px 6px;
`;

const TabArrowStyled = styled.button.attrs<IStyledAttributes>({
  [DATA_E2E_KEY]: "tab-arrow",
})`
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

export const LayoutRightTabs: FC<ILayoutRightTabs> = ({
  tabIndex = 0,
  setTabIndex,
  tabs,
}) => {
  const tabsNumber = tabs?.length || 0;
  const switchTabEvent = useEvent(newTabIndex => {
    setTabIndex?.(newTabIndex);
  });
  return (
    <>
      {tabs?.length && (
        <TabWrapperStyled>
          {tabIndex > 0 && (
            <TabArrowStyled
              type="button"
              aria-label="Previous tab"
              onClick={() => switchTabEvent(tabIndex - 1)}
            >{`<`}</TabArrowStyled>
          )}
          <TabStyled>
            {tabs?.[tabIndex]?.label || `Tab ${tabIndex + 1}`}
          </TabStyled>
          {tabIndex < tabsNumber - 1 && (
            <TabArrowStyled
              type="button"
              aria-label="Next tab"
              onClick={() => switchTabEvent(tabIndex + 1)}
            >{`>`}</TabArrowStyled>
          )}
        </TabWrapperStyled>
      )}
    </>
  );
};

export const TabsBottomInfoItemStyled = styled.span.attrs<IStyledAttributes>({
  "data-e2e": "tabs-bottom-info-item",
})`
  color: ${rydeColors.black};
  &.teal,
  .teal {
    color: ${tealMui._700};
  }
  &.grey,
  .grey {
    color: ${greyMui._700};
  }
  &.darkBlue,
  .darkBlue {
    color: ${blueMui._900};
  }
  &.darkRed,
  .darkRed {
    color: ${redMui._900};
  }

  display: flex;
  align-items: center;
  font-size: 12px;
  line-height: 1.2;
  padding-inline-end: 0;
  font-weight: 400;

  label {
    font-weight: 500;
    margin-inline-start: 5px;
  }
  label.passenger-label {
    margin-inline-start: 2px;
  }
  label.info.seats-in-brackets {
    margin-inline-start: 0;
  }
  svg {
    margin-inline-start: 5px;
  }
  .status_indicator,
  .status_Filled_indicator {
    margin-inline-start: 4px;
  }
  label.label {
    font-weight: 300;
  }
  label.course {
    font-weight: 400;
    display: flex;
    align-items: center;
    gap: 5px;
    color: ${blueMui.A200};
  }
  label.bolder {
    font-weight: 700;
  }
  .empty_station {
    font-size: 14px;
    color: ${blueGreyMui._800};
  }

  .independent_day_adjustment_indicator {
    font-size: 14px;
    color: ${blueGreyMui._800};
  }

  .saved-path-icon {
    font-size: 14px;
    color: ${blueGreyMui._600};
  }

  .item__wrapper {
    display: flex;
    gap: 4px;
    align-items: center;
  }

  .item__content {
    max-width: 230px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding-top: 7px;
  }
`;
